import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { getCall } from '../hooks/Network';
import { useTranslation } from 'react-i18next';
import { IoSearch } from 'react-icons/io5';
import { MenuItem, Select, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useOrderSearch } from '../hooks/Helper';
import { useDispatch, useSelector } from 'react-redux';
import { setSearchType } from '../slices/searchSlice';
import { flagEnabled } from '../util/flags';
import useFlagsHook from '../hooks/useFlags.hook';

const SearhAssociate = ({
  showIcon = false,
  onChange,
  placeholder = 'Search...',
  disabled,
  showCustomerList,
  setShowCustomerList,
}) => {
  const inputClass = 'form-control';
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { searchType } = useSelector((state) => state.search);
  const [searchCustomer, setSearchCustomer] = useState('');
  const [associateCustomers, setAssociateCustomers] = useState([]);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const { isFlagEnabled } = useFlagsHook();
  const theme = useTheme();
  const inputRef = useRef(null);
  const apiToken = localStorage.getItem('apiToken');
  const bearerToken = JSON.parse(apiToken)?.jwtToken;
  const { searchOrders } = useOrderSearch();

  const handleSubmit = async (associateId) => {
    if (searchType === 'customer') {
      onChange('search', associateId);
    }
    if (searchType === 'order') {
      const res = await searchOrders({
        type: 'ordernumber',
        search: searchCustomer.trim(),
      });
      if (res.data && res.data.data.length > 0) {
        const order = res.data.data[0];
        navigate(`/customers/${order.associateId}/Orders/${order.orderNumber}`);
      }
    }
    setShowCustomerList(false);
    setAssociateCustomers([]);
    setSearchCustomer('');
    setHighlightIndex(-1);
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!showCustomerList && searchType === 'customer') {
        setAssociateCustomers([]);
        setSearchCustomer('');
        setHighlightIndex(-1);
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [showCustomerList]);

  const fnGetCustomers = async (inputValue) => {
    if (inputValue.length >= 1) {
      let getData = {
        url: 'associateSearch',
        urlParams: { req: inputValue },
        headers: { Authorization: 'Bearer ' + bearerToken },
      };
      let cResponse = await getCall(getData);

      if (cResponse.data) {
        setAssociateCustomers(cResponse.data);
        // if (cResponse.data.length === 1) {
        //     handleSubmit(cResponse.data[0].associateId);
        // }
        if (cResponse.data) {
          setAssociateCustomers(cResponse.data);
          setHighlightIndex(0);
        }
      }
    }
  };

  const handleSearch = async () => {
    if (searchType === 'customer') {
      fnGetCustomers(searchCustomer);
      inputRef.current?.focus();
      return;
    }
    const res = await searchOrders({
      type: 'ordernumber',
      search: searchCustomer.trim(),
    });
    if (res.data && res.data.data.length > 0) {
      const order = res.data.data[0];
      navigate(`/customers/${order.associateId}/Orders/${order.orderNumber}`);
    }
  };

  // const handleKeyDown = (e) => {
  //     if (e.key === "Enter") {
  //         e.preventDefault();
  //         handleSearch();
  //     }
  // };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((prev) => Math.min(prev + 1, associateCustomers.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightIndex >= 0 && associateCustomers[highlightIndex]) {
        handleSubmit(associateCustomers[highlightIndex].associateId);
      } else {
        handleSearch();
      }
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchCustomer(value);

    if (value.length >= 1 && searchType === 'customer') {
      fnGetCustomers(value);
      setShowCustomerList(true);
    } else {
      setAssociateCustomers([]);
      setShowCustomerList(false);
    }
  };

  return (
    <>
      <div className='d-flex p-0 m-0 align-items-start w-100 '>
        {(isFlagEnabled('SEARCH_ORDER_DROPDOWN')) && (
          <Select
            size='small'
            value={searchType}
            onChange={(e) => {
              dispatch(setSearchType(e.target.value));
            }}
            sx={{ minWidth: 120, height: '46px', borderRadius: '8px 0 0 8px' }}
          >
            <MenuItem value='customer'>{t('associate')}</MenuItem>
            <MenuItem value='order'>{t('order')}</MenuItem>
          </Select>
        )}
        <div className='input-icon inp-div w-100 search-associate-type'>
          {/* onChange={(e) => setSearchCustomer(e.target.value)} */}
          <input
            ref={inputRef}
            className={inputClass + ' hdr-inp w-100 border-start-0'}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={
              searchType === 'customer' ? t('searchByNameHandleOrEmail') : placeholder
            }
            disabled={disabled}
            autoComplete='off'
            value={searchCustomer}
          />
          {associateCustomers.length > 0 && (
            <div
              className='w-100 autocomplete-items border'
              style={{ maxHeight: '500px', overflowY: 'auto' }}
            >
              {associateCustomers.map((item, index) => {
                return (
                  <div
                    key={item.associateId}
                    className={`autocomplete-item ${highlightIndex === index ? 'highlighted' : ''}`}
                    onClick={() => handleSubmit(item.associateId)}
                    onMouseEnter={() => setHighlightIndex(index)}
                    style={{
                      padding: '8px',
                      backgroundColor: highlightIndex === index ? '#f0f0f0' : 'white',
                      cursor: 'pointer',
                    }}
                  >
                    {item.firstName} {item.lastName} ({item.backOfficeId}) - {item.email}
                  </div>
                );
              })}
            </div>
          )}

          {associateCustomers.length === 0 &&
            searchCustomer.length > 0 &&
            searchType === 'customer' && (
              <div className='autocomplete-no-match'>{t('noMatchesFound')}</div>
            )}

          <span
            className='input-icon-addon search-icn'
            onClick={handleSearch}
            style={{ cursor: 'pointer' }}
          >
            <IoSearch size={20} color={theme.palette.text.primary} />
          </span>
        </div>
      </div>
    </>
  );
};

SearhAssociate.propTypes = {
  showIcon: PropTypes.bool,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  showCustomerList: PropTypes.bool,
  setShowCustomerList: PropTypes.func,
};
export default SearhAssociate;
